import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import InteractiveMap from './components/InteractiveMap'
import InteractivePoint from './components/InteractivePoint'
import { useSelector } from 'react-redux'
import { GAME_MODES, TYPE_POINTS } from './utils/constants'
import { NAME_SOUND_INTRO, TYPE_QUESTIONS, useGameSounds, useMediaQuery, useTranslate } from '@educaplay/core'
import { Slot } from '@educaplay/core/components'
import classes from './Game.module.scss'
import NavButtons from './components/NavButtons'
import MapProvider from './components/MapProvider'
import { useDispatch } from 'react-redux'
import { finishGame, goToQuestion } from '@educaplay/store/slices/gameSlice'
import { isUnansweredAnswer } from '@educaplay/store/utils/functions'
import { motion, AnimatePresence } from 'framer-motion'
import { preloadImages } from './utils/functions'
import clsx from 'clsx'
import NavegationQuestionProvider from './components/NavegationQuestionProvider'

function getNextQuestion({ questions, enabledGoBackCorrectAnswer }) {
  const matchQuestion = questions.find((question) => {
    if (isUnansweredAnswer(question.answer)) return true;

    if (enabledGoBackCorrectAnswer) {
      return !isUnansweredAnswer(question.answer) && question.checkAnswer(question.answer)
    }
    return !isUnansweredAnswer(question.answer) && !question.checkAnswer(question.answer)
  })

  return matchQuestion;
}
const customEvent = new Event("clickPassButton", { bubbles: true });

const Game = forwardRef(function Game({ onAnswer }, ref) {
 
  const currentMapImage = useSelector(state => {
    return state.game.questions[state.game.currentIndex].mapImage;
  });
  const currentIndex = useSelector(state => state.game.currentIndex);
  const currentQuestion = useSelector(state => state.game.questions[state.game.currentIndex]);
  const allQuestions = useSelector(state => state.game.questions);
  const isInfoScreen = (currentQuestion.type === TYPE_QUESTIONS.Selection && currentQuestion.mode === GAME_MODES.EXPLORE);
  const isSelectionGameScreen = (currentQuestion.type === TYPE_QUESTIONS.Selection && currentQuestion.mode === GAME_MODES.PLAY);
  const isCompleteEntryAnimation = useSelector(state => state.game.isCompleteEntryAnimation);
  const [mapCurrentQuestion, setMapCurrentQuestion] = useState(null);
  const mapRef = useRef(null);
  const dispatch = useDispatch();
  const screenId = currentQuestion.screenId;
  const questions = allQuestions.filter(question => question.screenId === screenId);
  const isAllScreenPointsVisited = questions.every(question => !isUnansweredAnswer(question.answer,{examMode: false}) && question.checkAnswer(question.answer));
  const t = useTranslate();
  const isTouchScreen = useMediaQuery("(hover:none)") || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0);
  const heightPoint = (currentQuestion.type === TYPE_QUESTIONS.Text && !isTouchScreen) ? -23 : 0;
  const [showMessage, setShowMessage] = useState(true);
  const [isInfoScreenActive, setIsInfoScreenActive] = useState(false);
  const [isInputOpen, setIsInputOpen] = useState(false);
  const hasBank = useSelector(state => state.game.hasBank);

  useImperativeHandle(ref, () => ({
    animateEntry: async () => {
      const images = new Set(allQuestions.map(question => question.mapImage));

      await preloadImages([...images]);
      mapRef.current.initialAnimation();
    },
    animateExit: async () => {
      mapRef.current.exitMapAnimation();
    }
  }));

  const clickHandler = (id) => setMapCurrentQuestion(id);

  const lastScreenRealIndex = allQuestions.findIndex(question => question.id === questions[questions.length - 1].id);
  const gameSound = useGameSounds();
  const [changeScreen, setChangeScreen] = useState(true);


  const passScreenHandler = async () => {
    const hasMoreScreens = allQuestions.length - 1 === lastScreenRealIndex ? false : true;
    if (!hasMoreScreens) {
      const finishParams = allQuestions.every((question) => question.mode === GAME_MODES.EXPLORE) ? { points: 100 } : null;
      dispatch(finishGame(finishParams));
      return;
    }

    gameSound.play(NAME_SOUND_INTRO);
    mapRef.current?.reset();
    if (allQuestions[lastScreenRealIndex].mapImage === allQuestions[lastScreenRealIndex + 1].mapImage) {
      await mapRef.current?.exitPointsAnimation();
    } else {
      await mapRef.current?.exitMapAnimation();
    }
    dispatch(goToQuestion(lastScreenRealIndex + 1));
    setChangeScreen(true);
  }


  useEffect(() => {
    if (changeScreen) {
      setChangeScreen(false);
      if (!isCompleteEntryAnimation) return;
      if (allQuestions[currentIndex]?.mapImage === allQuestions[currentIndex - 1]?.mapImage) {

        mapRef.current?.entryPointsAnimation();
      } else {
        mapRef.current?.entryMapAnimation();
      }
    }

  }, [screenId, isCompleteEntryAnimation, allQuestions, currentIndex, changeScreen])

  useEffect(() => {
    setMapCurrentQuestion(null);
  }, [screenId])


  const nextButtonHandler = (enablePassQuestion) => {
  
    if (enablePassQuestion) {
      if (!mapCurrentQuestion && !isSelectionGameScreen) {
        setMapCurrentQuestion(currentQuestion.id);
        return;
      }

      const indexScreen = questions.findIndex(question => question.id === allQuestions[currentIndex].id)
      const questionsOrdered = [...questions.slice(indexScreen + 1), ...questions.slice(0, indexScreen + 1)];

      const nextQuestion = getNextQuestion({ questions: questionsOrdered, enabledGoBackCorrectAnswer: isInfoScreen });
      if (!nextQuestion) return;

      setMapCurrentQuestion(null);

      setTimeout(() => {
        setMapCurrentQuestion(nextQuestion.id);
      });

      const nextQuestionIndex = allQuestions.findIndex(({ id }) => id === nextQuestion.id);

      dispatch(goToQuestion(nextQuestionIndex));
    }

    document.dispatchEvent(customEvent);

  }



  const points = questions.map(question => {

    const isExploreMode = question.mode === GAME_MODES.EXPLORE && question.type === TYPE_QUESTIONS.Selection;
    const isSelectionQuestion = question.type === TYPE_QUESTIONS.Selection && question.mode === GAME_MODES.PLAY;
    const questionType = isExploreMode
      ? TYPE_POINTS.INFO
      : isSelectionQuestion
        ? TYPE_POINTS.SELECTION
        : TYPE_POINTS.TEXT;

    const isCurrentQuestion = (currentQuestion.type === TYPE_QUESTIONS.Selection && currentQuestion.mode === GAME_MODES.PLAY) ?
      question.id === currentQuestion.id
      :
      question.id === mapCurrentQuestion;

    return (
      {
        x: question.x,
        y: question.y,
        id: question.id,
        component: (
          <InteractivePoint
            id={question.id}
            onAnswer={onAnswer}
            type={questionType}
            isCurrentQuestion={isCurrentQuestion}
            onClick={clickHandler}
            passScreenHandler={passScreenHandler}
            setShowMessage={setShowMessage}
            setIsInfoScreenActive={setIsInfoScreenActive}
            isInfoScreenActive={isInfoScreenActive}
            setIsInputOpen={setIsInputOpen}
          />
        )
      }
    )
  })


  return (
      <MapProvider mapRef={mapRef}>
        <NavegationQuestionProvider>
          <div className={classes.wrapper}>

            <AnimatePresence>
              {(showMessage && isSelectionGameScreen && !isAllScreenPointsVisited) && (
                <motion.div
                  className={clsx(classes.msgContainer, hasBank && classes.msgContainerBank)}
                  initial={{ opacity: 0, scale: 0.5, transition: { delay: 0.3 } }}
                  animate={{ opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeInOut" } }}
                  exit={{ opacity: 0, scale: 0.5 }}

                >
                  <div className={classes.msg}>
                    <span>{t('specific.specificPoint')}</span>
                  </div>
                </motion.div>)
              }
            </AnimatePresence>

            <InteractiveMap
              ref={mapRef}
              src={currentMapImage}
              points={points}
              pointOffsetY={heightPoint}
              animated
            />
            <NavButtons
              nextButtonHandler={nextButtonHandler}
              mapCurrentQuestion={mapCurrentQuestion}
              passScreenHandler={passScreenHandler}
              isInfoScreenActive={isInfoScreenActive}
              isInputOpen={isInputOpen}
            />
            <div>
              <Slot name="modal" />
              <Slot name="modal-top" />
            </div>
          </div>
        </NavegationQuestionProvider>
      </MapProvider>
  )
})

export default Game